import { useState } from 'react';
import { Phone } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Link } from 'react-router-dom';
import { useCallProvider } from '@/hooks/calls/useCallProvider';
import { normalizePhoneForSonax } from '@/lib/sonax/normalizePhone';
import { formatPhoneForDisplay } from '@/lib/phone-mask';

/**
 * Discagem manual em modo softphone — o equivalente do botão do webphone no header.
 *
 * O botão do Sonax abre o widget, que tem teclado próprio. O MicroSIP não pode ser aberto
 * "vazio" pelo navegador (o protocolo sip: só é acionado com um destino), então aqui o
 * teclado é nosso: digita o número, clica em Ligar, o MicroSIP abre já discando.
 *
 * NUNCA chama armSonax()/expandWidget(): em softphone o script do Sonax não deve ser
 * injetado, senão o ramal registra no navegador e concorre com o MicroSIP.
 */
export function SoftphoneDialPopover() {
  const { placeCall } = useCallProvider();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');

  const normalized = normalizePhoneForSonax(phone);
  const canDial = !!normalized;

  const handleDial = () => {
    if (!canDial) return;
    // placeCall é síncrono no caminho softphone (a URI precisa sair no mesmo turno do
    // clique). Fechar o popover só depois.
    void placeCall(phone);
    setPhone('');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="sonax-header-btn h-9 w-9 p-0 relative rounded-md text-white flex items-center justify-center cursor-pointer border-none outline-none"
          data-open={open ? 'true' : 'false'}
          style={{ zIndex: 100000, position: 'relative' }}
          title="Discar pelo MicroSIP"
        >
          <Phone className="h-4 w-4" />
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-64 space-y-3">
        <div className="space-y-1">
          <p className="text-sm font-medium">Discar pelo MicroSIP</p>
          <p className="text-xs text-muted-foreground">
            O MicroSIP abre no seu computador já chamando.
          </p>
        </div>

        <Input
          value={phone}
          autoFocus
          inputMode="tel"
          placeholder="(11) 98765-4321"
          onChange={(e) => setPhone(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleDial(); }}
        />

        {phone.trim() && (
          <p className={`text-xs ${canDial ? 'text-muted-foreground' : 'text-destructive'}`}>
            {canDial ? `Vai discar ${formatPhoneForDisplay(normalized)}` : 'Número inválido'}
          </p>
        )}

        <Button className="w-full" onClick={handleDial} disabled={!canDial}>
          Ligar
        </Button>

        <p className="text-xs text-muted-foreground">
          <Link to="/profile" className="underline hover:text-primary">
            Alterar no Perfil
          </Link>
        </p>
      </PopoverContent>
    </Popover>
  );
}
